import { Controller } from "@hotwired/stimulus"

// Displays the count of selected items in a bulk-actions context.
// Supports "select all across pages" functionality.
export default class extends Controller {
  static targets = ["count", "selectAllBanner", "selectAllLink", "clearLink"]
  static values = {
    totalCount: Number,       // Total items across all pages
    pageCount: Number,        // Items on current page
    allSelected: Boolean,     // Whether all items across pages are selected
    resourceName: { type: String, default: "items" }  // Resource name for hidden input ID
  }

  connect() {
    this.allSelectedValue = false
  }

  disconnect() {
    // Clean up hidden input when navigating away (Turbo Drive)
    const inputId = `select_all_${this.resourceNameValue}`
    const input = document.getElementById(inputId)
    if (input) input.remove()
  }

  // Called when bulk-actions dispatches update-ids event to this element
  updateCount(event) {
    const ids = event.detail?.ids || []
    const count = ids.length
    const allOnPage = event.detail?.all || false

    if (this.hasCountTarget) {
      if (this.allSelectedValue) {
        this.countTarget.textContent = this.totalCountValue
      } else {
        this.countTarget.textContent = count
      }
    }

    // Show/hide this element based on count
    if (count > 0 || this.allSelectedValue) {
      this.element.classList.remove("hidden")
    } else {
      this.element.classList.add("hidden")
    }

    // Show "select all" banner when all items on page are selected
    // but there are more items across pages
    if (this.hasSelectAllBannerTarget) {
      if (allOnPage && !this.allSelectedValue && this.totalCountValue > this.pageCountValue) {
        this.selectAllBannerTarget.classList.remove("hidden")
      } else {
        this.selectAllBannerTarget.classList.add("hidden")
      }
    }

    // If user deselects items, clear the "all selected" state
    if (!allOnPage && this.allSelectedValue) {
      this.allSelectedValue = false
      this.updateHiddenInput()
    }
  }

  // User clicked "Select all X items"
  selectAll(event) {
    event.preventDefault()
    this.allSelectedValue = true

    if (this.hasCountTarget) {
      this.countTarget.textContent = this.totalCountValue
    }

    if (this.hasSelectAllBannerTarget) {
      this.selectAllBannerTarget.classList.add("hidden")
    }

    this.updateHiddenInput()
  }

  // User clicked "Clear selection"
  clearSelection(event) {
    event.preventDefault()
    this.allSelectedValue = false

    // Trigger deselection in bulk-actions
    const bulkActionsEl = this.element.closest('[data-controller~="bulk-actions"]')
    if (bulkActionsEl) {
      const checkboxes = bulkActionsEl.querySelectorAll('[data-bulk-actions-target="checkbox"]')
      checkboxes.forEach(cb => cb.checked = false)

      // Trigger update
      const selectAllCheckbox = bulkActionsEl.querySelector('[data-bulk-actions-target="selectAllCheckbox"]')
      if (selectAllCheckbox) {
        selectAllCheckbox.checked = false
        selectAllCheckbox.indeterminate = false
        selectAllCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    this.element.classList.add("hidden")
    this.updateHiddenInput()
  }

  updateHiddenInput() {
    // Update a hidden input that the export form can read
    const inputId = `select_all_${this.resourceNameValue}`
    let input = document.getElementById(inputId)
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.id = inputId
      input.name = 'select_all'
      document.body.appendChild(input)
    }
    input.value = this.allSelectedValue ? 'true' : 'false'
  }
}
